var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { library } from "@fortawesome/fontawesome-svg-core";
import { faAngleDoubleDown, faAngleDoubleUp } from "@fortawesome/free-solid-svg-icons";
import { computed, onMounted, ref } from "vue";
import { useConfirmDialog } from "@/composables/confirmDialog";
import { useToast } from "@/composables/toast";
import localize from "@/utils/localization";
import { bytesLabelFormatter, bytesValueFormatter } from "./Charts/formatters";
import { purgeDatasetById, undeleteDatasetById } from "./service";
library.add(faAngleDoubleUp, faAngleDoubleDown);
export function useDatasetsToDisplay() {
    const numberOfDatasetsToDisplayOptions = [10, 20, 50];
    const numberOfDatasetsToDisplay = ref(numberOfDatasetsToDisplayOptions[0] || 10);
    const numberOfDatasetsLimit = Math.max(...numberOfDatasetsToDisplayOptions);
    const datasetsSizeSummaryMap = new Map();
    const topNDatasetsBySizeData = ref(null);
    function isRecoverableDataPoint(dataPoint) {
        if (dataPoint) {
            const datasetSizeSummary = datasetsSizeSummaryMap.get(dataPoint.id || "");
            return (datasetSizeSummary === null || datasetSizeSummary === void 0 ? void 0 : datasetSizeSummary.deleted) || dataPoint.id === "deleted";
        }
        return false;
    }
    const { success: successToast, error: errorToast } = useToast();
    const { confirm } = useConfirmDialog();
    function onUndeleteDataset(reloadData, datasetId) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const result = yield undeleteDatasetById(datasetId);
                const dataset = datasetsSizeSummaryMap.get(datasetId);
                if (dataset && !result.deleted) {
                    dataset.deleted = result.deleted;
                    datasetsSizeSummaryMap.set(datasetId, dataset);
                    successToast(localize("Dataset undeleted successfully."));
                    reloadData();
                }
            }
            catch (error) {
                errorToast(`${error}`, localize("An error occurred while undeleting the dataset."));
            }
        });
    }
    function onPermanentlyDeleteDataset(reloadData, datasetId) {
        return __awaiter(this, void 0, void 0, function* () {
            const confirmed = yield confirm(localize("Are you sure you want to permanently delete this dataset? This action cannot be undone."), {
                title: localize("Permanently delete dataset?"),
                okVariant: "danger",
                okTitle: localize("Permanently delete"),
                cancelTitle: localize("Cancel"),
            });
            if (!confirmed) {
                return;
            }
            try {
                const result = yield purgeDatasetById(datasetId);
                const dataset = datasetsSizeSummaryMap.get(datasetId);
                if (dataset && result) {
                    datasetsSizeSummaryMap.delete(datasetId);
                    successToast(localize("Dataset permanently deleted successfully."));
                    reloadData();
                }
            }
            catch (error) {
                errorToast(`${error}`, localize("An error occurred while permanently deleting the dataset."));
            }
        });
    }
    return {
        numberOfDatasetsToDisplayOptions,
        numberOfDatasetsToDisplay,
        numberOfDatasetsLimit,
        datasetsSizeSummaryMap,
        topNDatasetsBySizeData,
        isRecoverableDataPoint,
        onUndeleteDataset,
        onPermanentlyDeleteDataset,
    };
}
export function useDataLoading() {
    const isLoading = ref(true);
    const loadDataOnMount = (dataLoader) => {
        onMounted(() => __awaiter(this, void 0, void 0, function* () {
            isLoading.value = true;
            yield dataLoader();
            isLoading.value = false;
        }));
    };
    return {
        isLoading,
        loadDataOnMount,
    };
}
export function buildTopNDatasetsBySizeData(datasetsSizeSummary, n) {
    const topTenDatasetsBySize = datasetsSizeSummary.sort((a, b) => b.size - a.size).slice(0, n);
    return topTenDatasetsBySize.map((dataset) => ({
        id: dataset.id,
        label: dataset.name,
        value: dataset.size,
    }));
}
export const byteFormattingForChart = {
    "enable-selection": true,
    labelFormatter: bytesLabelFormatter,
    valueFormatter: bytesValueFormatter,
};
export function useAdvancedFiltering() {
    const isAdvanced = ref(false);
    function toggleAdvanced() {
        isAdvanced.value = !isAdvanced.value;
    }
    const inputGroupClasses = computed(() => {
        return ["float-right", "auto"];
    });
    return {
        faAngleDoubleUp,
        faAngleDoubleDown,
        isAdvanced,
        inputGroupClasses,
        toggleAdvanced,
    };
}
